import * as S from "sanctuary";
import type { Either } from "sanctuary";

import {
  deserializeArrayToSchema,
  deserializeMetadataToSchema,
  deserializeToSchema,
} from "../deserializer";
import type {
  HttpResponse,
  ResponseError,
  ResponseHandler,
  ResponseSchema,
} from "./types";

/** envelopedListResponse's Right: the deserialized data and meta, each an Either. */
type EnvelopedList = {
  data: Either<Error, unknown>;
  meta: Either<Error, unknown>;
};

// Adds the response's status and parsed body to a deserializer's Left.
const withResponse = <T>(
  result: Either<Error, T>,
  { data, headers }: HttpResponse
): Either<ResponseError, T> =>
  S.isLeft(result)
    ? S.Left(
        Object.assign(result.value, {
          status: headers?.status ?? null,
          body: data ?? null,
        })
      )
    : result;

/** A `{ data }` body, deserialized against `schema`. */
export const envelopedResponse =
  (schema: ResponseSchema): ResponseHandler<unknown> =>
  (response) =>
    withResponse(deserializeToSchema(schema)(response), response);

/**
 * A `{ data: [...], meta }` body. Always a Right, holding the deserialized data
 * and meta as two Eithers.
 */
export const envelopedListResponse =
  (
    schema: ResponseSchema,
    metaSchema: ResponseSchema
  ): ResponseHandler<EnvelopedList> =>
  (response) =>
    S.Right({
      data: deserializeArrayToSchema(schema)(response),
      meta: deserializeMetadataToSchema(metaSchema)(response),
    });

/** A body without a `data` envelope, deserialized against `schema`. */
export const unenvelopedResponse =
  (schema: ResponseSchema): ResponseHandler<unknown> =>
  (response) =>
    withResponse(
      deserializeToSchema(schema)({
        data: { data: response.data },
        headers: response.headers,
      }),
      response
    );

/** The body unchanged, or a Left for a non-2xx response. */
export const rawResponse: ResponseHandler<unknown> = ({ data, headers }) =>
  headers?.ok
    ? S.Right(data)
    : S.Left(
        Object.assign(new Error(headers?.statusText || "No reason found"), {
          status: headers?.status ?? null,
          body: data ?? null,
        })
      );
